import { AlertCircle, Info, X } from "lucide-react";
import React from "react";
import DiscordIcon from "src/components/ui/icons/discord";
import SlackIcon from "src/components/ui/icons/slack";
import { WarningAlert } from "src/components/ui/status-alert";
import { useMe } from "src/context/MeContext";
import { getBotName } from "src/utils/brandTheme";

import DiscordWebhookTypeahead from "./DiscordWebhookTypeahead";
import SlackChannelTypeahead from "./SlackChannelTypeahead";

export interface SlackChannel {
  id: string;
  name: string;
  isMember: boolean;
}

export interface DiscordWebhook {
  id: string;
  name: string;
}

interface DigestNotificationDestinationsProps {
  /** Whether the org has an approved Slack integration */
  hasSlackIntegration: boolean;
  /** Whether the org has an approved Discord integration */
  hasDiscordIntegration: boolean;
  /** Currently selected Slack channels */
  selectedSlackChannels: SlackChannel[];
  /** Currently selected Discord webhooks */
  selectedDiscordWebhooks: DiscordWebhook[];
  /** Available Discord webhooks to choose from */
  availableDiscordWebhooks: DiscordWebhook[];
  /** Callback when a Slack channel is added */
  onSlackChannelAdd: (channel: SlackChannel) => void;
  /** Callback when a Slack channel is removed */
  onSlackChannelRemove: (channelId: string) => void;
  /** Callback when a Discord webhook is added */
  onDiscordWebhookAdd: (webhook: DiscordWebhook) => void;
  /** Callback when a Discord webhook is removed */
  onDiscordWebhookRemove: (webhookId: string) => void;
  /** Visual variant for the "no integrations" message */
  noIntegrationsVariant?: "simple" | "banner";
}

export function DigestNotificationDestinations({
  hasSlackIntegration,
  hasDiscordIntegration,
  selectedSlackChannels,
  selectedDiscordWebhooks,
  availableDiscordWebhooks,
  onSlackChannelAdd,
  onSlackChannelRemove,
  onDiscordWebhookAdd,
  onDiscordWebhookRemove,
  noIntegrationsVariant = "simple",
}: DigestNotificationDestinationsProps) {
  const me = useMe();
  const brandTheme = me.organization?.brandTheme ?? "TIERZERO";

  // No integrations connected
  if (!hasSlackIntegration && !hasDiscordIntegration) {
    if (noIntegrationsVariant === "banner") {
      return (
        <div className="flex items-start gap-3 rounded-md bg-amber-900/20 border border-amber-700/40 p-3">
          <Info className="h-5 w-5 text-amber-500 mt-0.5 shrink-0" />
          <p className="text-sm text-amber-200/90">
            Connect <span className="font-medium text-amber-400">Slack</span> or{" "}
            <span className="font-medium text-amber-400">Discord</span> in{" "}
            <a
              href="/settings/integrations"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:text-primary/80 underline"
            >
              Integrations
            </a>
            {" "}to enable notifications.
          </p>
        </div>
      );
    }

    return (
      <div className="text-sm text-muted-foreground">
        Connect <span className="text-amber-600">Slack</span> or{" "}
        <span className="text-amber-600">Discord</span> in{" "}
        <a href="/settings/integrations" className="text-primary hover:text-primary/80 underline">
          Integrations
        </a>{" "}
        to set Notification Destinations.
      </div>
    );
  }

  return (
    <>
      {/* Slack Options */}
      {hasSlackIntegration && (
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground min-w-fit">
              <SlackIcon className="h-5 w-5" />
              <span>Slack</span>
            </div>
            <div className="flex-1 max-w-md">
              <SlackChannelTypeahead
                value=""
                onChange={(channelId, channelName, isMember) => {
                  if (!selectedSlackChannels.some((ch) => ch.id === channelId)) {
                    onSlackChannelAdd({ id: channelId, name: channelName, isMember });
                  }
                }}
                excludeChannelIds={selectedSlackChannels.map((ch) => ch.id)}
              />
            </div>
          </div>

          {/* Selected channels chips */}
          {selectedSlackChannels.length > 0 && (
            <div className="flex flex-wrap gap-2 ml-7">
              {selectedSlackChannels.map((channel) => (
                <div
                  key={channel.id}
                  className="flex items-center gap-1.5 px-2 py-1 bg-accent border border-border rounded-md text-sm"
                >
                  <span className="text-muted-foreground">#</span>
                  <span className="text-foreground">{channel.name}</span>
                  {!channel.isMember && <AlertCircle className="h-3 w-3 text-yellow-500" />}
                  <button
                    type="button"
                    onClick={() => onSlackChannelRemove(channel.id)}
                    className="ml-1 hover:bg-red-500/20 rounded p-0.5 transition-colors"
                  >
                    <X className="h-3 w-3 text-muted-foreground hover:text-red-400" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Warning for non-member channels */}
          {selectedSlackChannels.some((ch) => !ch.isMember) && (
            <WarningAlert size="sm" className="ml-7">
              @{getBotName(brandTheme)} is not a member of some selected channels. Please add @
              {getBotName(brandTheme)} to receive digest notifications.
            </WarningAlert>
          )}
        </div>
      )}

      {/* Discord Options */}
      {hasDiscordIntegration && (
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground min-w-fit">
              <DiscordIcon className="h-5 w-5" />
              <span>Discord</span>
            </div>
            <div className="flex-1 max-w-md">
              <DiscordWebhookTypeahead
                webhooks={availableDiscordWebhooks}
                excludeWebhookIds={selectedDiscordWebhooks.map((wh) => wh.id)}
                onSelect={onDiscordWebhookAdd}
              />
            </div>
          </div>

          {/* Selected Discord webhooks chips */}
          {selectedDiscordWebhooks.length > 0 && (
            <div className="flex flex-wrap gap-2 ml-7">
              {selectedDiscordWebhooks.map((webhook) => (
                <div
                  key={webhook.id}
                  className="flex items-center gap-1.5 px-2 py-1 bg-accent border border-border rounded-md text-sm"
                >
                  <DiscordIcon className="h-4 w-4" />
                  <span className="text-foreground">{webhook.name}</span>
                  <button
                    type="button"
                    onClick={() => onDiscordWebhookRemove(webhook.id)}
                    className="ml-1 hover:bg-red-500/20 rounded p-0.5 transition-colors"
                  >
                    <X className="h-3 w-3 text-muted-foreground hover:text-red-400" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );
}

export default DigestNotificationDestinations;
